import { DEFAULT_THEME_ID, isValidThemeId } from "./themes";

const THEME_KEY = "drawing-conclusions:theme";
const BG_IMAGE_KEY = "drawing-conclusions:bg-image";

type Listener = () => void;

class ThemeManager {
  private listeners = new Set<Listener>();

  get current(): string {
    const stored = safeGet(THEME_KEY);
    return stored && isValidThemeId(stored) ? stored : DEFAULT_THEME_ID;
  }

  get backgroundImageEnabled(): boolean {
    return safeGet(BG_IMAGE_KEY) !== "off";
  }

  setTheme(id: string): void {
    if (!isValidThemeId(id)) return;
    document.documentElement.setAttribute("data-theme", id);
    safeSet(THEME_KEY, id);
    this.notify();
  }

  setBackgroundImageEnabled(enabled: boolean): void {
    if (enabled) {
      document.documentElement.removeAttribute("data-bg-image");
      safeSet(BG_IMAGE_KEY, "on");
    } else {
      document.documentElement.setAttribute("data-bg-image", "off");
      safeSet(BG_IMAGE_KEY, "off");
    }
    this.notify();
  }

  /** Applies the persisted theme/background choice - the inline <head> script already
   * did this before first paint, so this just keeps this module's own state in sync. */
  applyStored(): void {
    document.documentElement.setAttribute("data-theme", this.current);
    if (!this.backgroundImageEnabled) document.documentElement.setAttribute("data-bg-image", "off");
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private notify(): void {
    for (const listener of this.listeners) listener();
  }
}

function safeGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // ignore
  }
}

export const themeManager = new ThemeManager();
